import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import { ncbClient } from '../../services/ncbClient';
import { getCartSummary } from '../../utils/cart';

const { FiTerminal, FiCopy, FiDownload, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiInfo, FiX, FiKey, FiZap, FiDatabase } = FiIcons;

const AdminDebugPanel = () => {
  const [logs, setLogs] = useState([]);
  const [debugData, setDebugData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('logs');
  const [apiTestResult, setApiTestResult] = useState(null);

  // Collect comprehensive debug information
  const collectDebugInfo = async () => {
    setIsLoading(true);
    try {
      const cartSummary = getCartSummary();
      
      const debugInfo = {
        timestamp: new Date().toISOString(),
        environment: {
          mode: import.meta.env.MODE,
          base: import.meta.env.BASE_URL,
          dev: import.meta.env.DEV,
          prod: import.meta.env.PROD,
          ssr: import.meta.env.SSR,
        },
        environmentVariables: {
          NOCODEBACKEND_URL: import.meta.env.VITE_NOCODEBACKEND_URL || 'NOT_SET',
          NOCODEBACKEND_INSTANCE: import.meta.env.VITE_NOCODEBACKEND_INSTANCE || 'NOT_SET',
          NOCODEBACKEND_KEY: import.meta.env.VITE_NOCODEBACKEND_KEY ? 'SET' : 'NOT_SET',
          DROPBOX_ACCESS_TOKEN: import.meta.env.VITE_DROPBOX_ACCESS_TOKEN ? 'SET' : 'NOT_SET',
          DROPBOX_APP_KEY: import.meta.env.VITE_DROPBOX_APP_KEY || 'NOT_SET',
          DROPBOX_APP_SECRET: import.meta.env.VITE_DROPBOX_APP_SECRET ? 'SET' : 'NOT_SET',
          APP_NAME: import.meta.env.VITE_APP_NAME || 'NOT_SET',
          APP_URL: import.meta.env.VITE_APP_URL || 'NOT_SET',
          ENABLE_DROPBOX: import.meta.env.VITE_ENABLE_DROPBOX || 'NOT_SET',
          ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS || 'NOT_SET',
          ENABLE_PWA: import.meta.env.VITE_ENABLE_PWA || 'NOT_SET',
        },
        browser: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        localStorage: {
          authToken: localStorage.getItem('auth-token') ? 'SET' : 'NOT_SET',
          userData: localStorage.getItem('user-data') ? 'SET' : 'NOT_SET',
          cartStorage: localStorage.getItem('shophub_cart') ? 'SET' : 'NOT_SET',
          authStorage: localStorage.getItem('auth-storage') ? 'SET' : 'NOT_SET',
          keys: Object.keys(localStorage),
        },
        cart: cartSummary,
        sessionStorage: {
          keys: Object.keys(sessionStorage),
        },
        performance: {
          loadTime: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : 'N/A',
          domContentLoaded: performance.timing ? performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart : 'N/A',
        },
        network: {
          connection: navigator.connection ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
          } : 'NOT_AVAILABLE',
        },
        consoleErrors: window.collectedErrors || [],
        consoleWarnings: window.collectedWarnings || [],
      };

      // Get API status using health check
      try {
        const healthResult = await ncbClient.healthCheck();
        debugInfo.apiStatus = {
          url: `${import.meta.env.VITE_NOCODEBACKEND_URL || 'https://openapi.nocodebackend.com'}/read/products?Instance=54230_nocodebackend&limit=1`,
          status: healthResult.status,
          reachable: healthResult.ok,
          error: healthResult.error,
        };
      } catch (error) {
        debugInfo.apiStatus = {
          url: `${import.meta.env.VITE_NOCODEBACKEND_URL || 'https://openapi.nocodebackend.com'}/read/products?Instance=54230_nocodebackend&limit=1`,
          status: 'FAILED',
          reachable: false,
          error: error.message,
        };
      }

      setDebugData(debugInfo);
      
      // Add to logs
      const newLog = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        type: 'info',
        message: 'Debug information collected successfully',
        data: debugInfo,
      };
      setLogs(prev => [newLog, ...prev].slice(0, 100)); // Keep last 100 logs
      
    } catch (error) {
      const errorLog = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        type: 'error',
        message: 'Failed to collect debug information',
        error: error.message,
      };
      setLogs(prev => [errorLog, ...prev]);
      toast.error('Failed to collect debug information');
    } finally {
      setIsLoading(false);
    }
  };

  // Test API connection with multiple endpoints
  const testApiConnection = async () => {
    setIsLoading(true);
    try {
      const testEndpoints = [
        {
          name: 'Products Endpoint',
          test: () => ncbClient.readProducts({ limit: 1 })
        },
        {
          name: 'Categories Endpoint',
          test: () => ncbClient.readCategories()
        }
      ];
      
      const results = [];
      let successCount = 0;
      
      for (const endpoint of testEndpoints) {
        try {
          const startTime = Date.now();
          const response = await endpoint.test();
          const endTime = Date.now();
          
          const result = {
            endpoint: endpoint.name,
            status: 'OK',
            ok: true,
            latency: endTime - startTime,
            items: response?.data?.length || 0,
          };
          
          results.push(result);
          successCount++;
          
        } catch (error) {
          results.push({
            endpoint: endpoint.name,
            status: error.response?.status || 'ERROR',
            ok: false,
            error: error.message,
          });
        }
      }
      
      const overallResult = {
        successCount,
        totalCount: testEndpoints.length,
        results,
        overallStatus: successCount > 0 ? 'PARTIAL_SUCCESS' : 'FAILURE',
        ok: successCount > 0
      };
      
      setApiTestResult(overallResult);
      
      const newLog = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        type: successCount > 0 ? 'success' : 'error',
        message: `API Test: ${successCount}/${testEndpoints.length} endpoints successful`,
        data: overallResult,
      };
      setLogs(prev => [newLog, ...prev].slice(0, 100));
      
      toast.success(`${successCount}/${testEndpoints.length} API endpoints working!`);
    } catch (error) {
      const result = {
        status: 'ERROR',
        ok: false,
        error: error.message,
      };
      
      setApiTestResult(result);
      
      const newLog = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        type: 'error',
        message: 'API Test: Connection Error',
        error: error.message,
        data: result,
      };
      setLogs(prev => [newLog, ...prev].slice(0, 100));
      
      toast.error('API connection error');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize console error/warning collection
  useEffect(() => {
    // Store original console methods
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    // Initialize collections if not exist
    if (!window.collectedErrors) window.collectedErrors = [];
    if (!window.collectedWarnings) window.collectedWarnings = [];

    // Override console methods to collect logs
    console.error = (...args) => {
      originalError.apply(console, args);
      window.collectedErrors.push({
        timestamp: new Date().toISOString(),
        message: args.join(' '),
        stack: new Error().stack,
      });
      if (window.collectedErrors.length > 50) {
        window.collectedErrors.shift();
      }
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      window.collectedWarnings.push({
        timestamp: new Date().toISOString(),
        message: args.join(' '),
      });
      if (window.collectedWarnings.length > 50) {
        window.collectedWarnings.shift();
      }
    };

    // Collect initial debug info
    collectDebugInfo();

    // Restore original methods on cleanup
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      console.log = originalLog;
    };
  }, []);

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Download debug info as JSON file
  const downloadDebugInfo = () => {
    const data = {
      debugData,
      logs,
      timestamp: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-info-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Debug info downloaded!');
  };

  // Format debug data for ChatGPT
  const formatForChatGPT = () => {
    const formatted = `
=== SHOphUB ADMIN DEBUG REPORT ===
Generated: ${new Date().toISOString()}
Issue Description: [Please describe your issue here]

=== ENVIRONMENT CONFIGURATION ===
Mode: ${debugData.environment?.mode || 'N/A'}
Development: ${debugData.environment?.dev || 'N/A'}
Production: ${debugData.environment?.prod || 'N/A'}

=== ENVIRONMENT VARIABLES ===
NoCodeBackend URL: ${debugData.environmentVariables?.NOCODEBACKEND_URL || 'N/A'}
NoCodeBackend Instance: ${debugData.environmentVariables?.NOCODEBACKEND_INSTANCE || 'N/A'}
NoCodeBackend Key: ${debugData.environmentVariables?.NOCODEBACKEND_KEY || 'N/A'}
Dropbox Access Token: ${debugData.environmentVariables?.DROPBOX_ACCESS_TOKEN || 'N/A'}
Dropbox App Key: ${debugData.environmentVariables?.DROPBOX_APP_KEY || 'N/A'}
Dropbox App Secret: ${debugData.environmentVariables?.DROPBOX_APP_SECRET || 'N/A'}
App Name: ${debugData.environmentVariables?.VITE_APP_NAME || 'N/A'}
App URL: ${debugData.environmentVariables?.VITE_APP_URL || 'N/A'}

=== BROWSER INFORMATION ===
User Agent: ${debugData.browser?.userAgent || 'N/A'}
Language: ${debugData.browser?.language || 'N/A'}
Platform: ${debugData.browser?.platform || 'N/A'}
Screen Resolution: ${debugData.browser?.screenResolution || 'N/A'}
Viewport: ${debugData.browser?.viewport || 'N/A'}
Timezone: ${debugData.browser?.timezone || 'N/A'}
Online: ${debugData.browser?.onLine || 'N/A'}
Cookies Enabled: ${debugData.browser?.cookieEnabled || 'N/A'}

=== API STATUS ===
API URL: ${debugData.apiStatus?.url || 'N/A'}
API Status: ${debugData.apiStatus?.status || 'N/A'}
API Reachable: ${debugData.apiStatus?.reachable || 'N/A'}
API Error: ${debugData.apiStatus?.error || 'N/A'}

=== STORAGE INFORMATION ===
Local Storage Keys: ${debugData.localStorage?.keys?.join(', ') || 'N/A'}
Auth Token: ${debugData.localStorage?.authToken || 'N/A'}
User Data: ${debugData.localStorage?.userData || 'N/A'}
Cart Storage: ${debugData.localStorage?.cartStorage || 'N/A'}
Session Storage Keys: ${debugData.sessionStorage?.keys?.join(', ') || 'N/A'}

=== CART SUMMARY ===
Items: ${debugData.cart?.items || 'N/A'}
Total Items: ${debugData.cart?.itemCount || 'N/A'}
Total Value: $${debugData.cart?.total?.toFixed(2) || '0.00'}

=== PERFORMANCE METRICS ===
Page Load Time: ${debugData.performance?.loadTime || 'N/A'}ms
DOM Content Loaded: ${debugData.performance?.domContentLoaded || 'N/A'}ms

=== NETWORK INFORMATION ===
Connection Type: ${debugData.network?.connection?.effectiveType || 'N/A'}
Downlink: ${debugData.network?.connection?.downlink || 'N/A'}Mbps
RTT: ${debugData.network?.connection?.rtt || 'N/A'}ms

=== RECENT ERRORS ===
${debugData.consoleErrors?.map((error, index) => `
Error ${index + 1}:
  Time: ${error.timestamp}
  Message: ${error.message}
  Stack: ${error.stack}
`).join('') || 'No errors collected'}

=== RECENT WARNINGS ===
${debugData.consoleWarnings?.map((warning, index) => `
Warning ${index + 1}:
  Time: ${warning.timestamp}
  Message: ${warning.message}
`).join('') || 'No warnings collected'}

=== RECENT LOGS ===
${logs.slice(0, 10).map((log, index) => `
Log ${index + 1}:
  Time: ${log.timestamp}
  Type: ${log.type}
  Message: ${log.message}
  ${log.error ? `Error: ${log.error}` : ''}
`).join('') || 'No logs available'}

=== STEPS TO REPRODUCE ===
[Please list the steps to reproduce the issue]

=== EXPECTED BEHAVIOR ===
[Please describe what should happen]

=== ACTUAL BEHAVIOR ===
[Please describe what actually happened]

=== ADDITIONAL CONTEXT ===
[Please provide any additional information that might be helpful]

END OF DEBUG REPORT
    `.trim();

    copyToClipboard(formatted);
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'error': return FiAlertCircle;
      case 'warning': return FiAlertCircle;
      case 'success': return FiCheckCircle;
      default: return FiInfo;
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'success': return 'text-green-600 bg-green-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <SafeIcon icon={FiTerminal} className="w-5 h-5 mr-2" />
            Debug & Logs
          </h2>
          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={testApiConnection}
              disabled={isLoading}
              className="flex items-center space-x-2 px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              <span>Test API</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={collectDebugInfo}
              disabled={isLoading}
              className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={formatForChatGPT}
              className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4" />
              <span>Copy for ChatGPT</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={downloadDebugInfo}
              className="flex items-center space-x-2 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Download</span>
            </motion.button>
          </div>
        </div>
        <div className="flex space-x-1 px-4">
          {['logs', 'environment', 'storage', 'network'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'logs' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-md font-semibold text-gray-700">System Logs</h3>
              <span className="text-sm text-gray-500">Last {logs.length} entries</span>
            </div>
            
            {/* API Test Result */}
            {apiTestResult && (
              <div className={`p-4 rounded-lg mb-4 ${apiTestResult.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center">
                  <SafeIcon icon={apiTestResult.ok ? FiCheckCircle : FiAlertCircle} className={`w-5 h-5 mr-2 ${apiTestResult.ok ? 'text-green-600' : 'text-red-600'}`} />
                  <h4 className="font-medium text-gray-800">API Connection Test Result</h4>
                </div>
                <div className="mt-2 text-sm">
                  <p>Endpoints: <span className="font-medium">{apiTestResult.successCount}/{apiTestResult.totalCount} successful</span></p>
                  {apiTestResult.results && (
                    <div className="mt-2">
                      {apiTestResult.results.map((result, index) => (
                        <div key={index} className="flex items-center justify-between py-1">
                          <span>{result.endpoint}:</span>
                          <span className={`font-medium ${result.ok ? 'text-green-600' : 'text-red-600'}`}>
                            {result.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {apiTestResult.error && <p>Error: <span className="font-medium text-red-600">{apiTestResult.error}</span></p>}
                </div>
              </div>
            )}
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiInfo} className="w-12 h-12 mx-auto mb-2" />
                  <p>No logs available</p>
                </div>
              ) : (
                logs.map((log) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className={`p-1 rounded ${getLogColor(log.type)}`}>
                      <SafeIcon icon={getLogIcon(log.type)} className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800">{log.message}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {log.error && (
                        <p className="text-sm text-red-600 mt-1">{log.error}</p>
                      )}
                      {log.data && (
                        <details className="mt-2">
                          <summary className="text-sm text-gray-600 cursor-pointer">View details</summary>
                          <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'environment' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">Environment Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Build Environment</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Mode:</dt>
                      <dd className="font-medium">{debugData.environment?.mode || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Development:</dt>
                      <dd className="font-medium">{debugData.environment?.dev ? 'Yes' : 'No'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Production:</dt>
                      <dd className="font-medium">{debugData.environment?.prod ? 'Yes' : 'No'}</dd>
                    </div>
                  </dl>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Environment Variables</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">NoCodeBackend Key:</dt>
                      <dd className={`font-medium ${debugData.environmentVariables?.NOCODEBACKEND_KEY === 'SET' ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.environmentVariables?.NOCODEBACKEND_KEY || 'N/A'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Dropbox Token:</dt>
                      <dd className={`font-medium ${debugData.environmentVariables?.DROPBOX_ACCESS_TOKEN === 'SET' ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.environmentVariables?.DROPBOX_ACCESS_TOKEN || 'N/A'}
                      </dd>
                    </div>
                  </dl>
                  
                  {/* API Key Warning */}
                  {debugData.environmentVariables?.NOCODEBACKEND_KEY !== 'SET' && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start">
                        <SafeIcon icon={FiKey} className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                        <div>
                          <h5 className="font-medium text-yellow-800">API Key Missing</h5>
                          <p className="text-sm text-yellow-700 mt-1">
                            NoCodeBackend API key is not set. Please configure VITE_NOCODEBACKEND_KEY in your environment variables.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">API Status</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">API URL:</dt>
                    <dd className="font-medium">{debugData.apiStatus?.url || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Status:</dt>
                    <dd className={`font-medium ${debugData.apiStatus?.reachable ? 'text-green-600' : 'text-red-600'}`}>
                      {debugData.apiStatus?.status || 'N/A'}
                    </dd>
                  </div>
                  {debugData.apiStatus?.error && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Error:</dt>
                      <dd className="font-medium text-red-600">{debugData.apiStatus.error}</dd>
                    </div>
                  )}
                  
                  {/* API Status Warning */}
                  {!debugData.apiStatus?.reachable && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start">
                        <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                        <div>
                          <h5 className="font-medium text-red-800">API Connection Failed</h5>
                          <p className="text-sm text-red-700 mt-1">
                            Unable to connect to NoCodeBackend API. Please check your API key and network connection.
                          </p>
                          <button 
                            onClick={testApiConnection}
                            className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            Test Connection
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'storage' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">Storage Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Local Storage</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Auth Token:</dt>
                      <dd className={`font-medium ${debugData.localStorage?.authToken === 'SET' ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.localStorage?.authToken || 'N/A'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">User Data:</dt>
                      <dd className={`font-medium ${debugData.localStorage?.userData === 'SET' ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.localStorage?.userData || 'N/A'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Cart Storage:</dt>
                      <dd className={`font-medium ${debugData.localStorage?.cartStorage === 'SET' ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.localStorage?.cartStorage || 'N/A'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Keys:</dt>
                      <dd className="font-medium">{debugData.localStorage?.keys?.join(', ') || 'None'}</dd>
                    </div>
                  </dl>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Cart Summary</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Items:</dt>
                      <dd className="font-medium">{debugData.cart?.items || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Total Items:</dt>
                      <dd className="font-medium">{debugData.cart?.itemCount || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Total Value:</dt>
                      <dd className="font-medium">${debugData.cart?.total?.toFixed(2) || '0.00'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">Session Storage</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Keys:</dt>
                    <dd className="font-medium">{debugData.sessionStorage?.keys?.join(', ') || 'None'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'network' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">Network Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Browser Info</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">User Agent:</dt>
                      <dd className="font-medium text-xs truncate max-w-xs">{debugData.browser?.userAgent || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Language:</dt>
                      <dd className="font-medium">{debugData.browser?.language || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Online:</dt>
                      <dd className={`font-medium ${debugData.browser?.onLine ? 'text-green-600' : 'text-red-600'}`}>
                        {debugData.browser?.onLine ? 'Yes' : 'No'}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Connection</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Type:</dt>
                      <dd className="font-medium">{debugData.network?.connection?.effectiveType || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Downlink:</dt>
                      <dd className="font-medium">{debugData.network?.connection?.downlink || 'N/A'} Mbps</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">RTT:</dt>
                      <dd className="font-medium">{debugData.network?.connection?.rtt || 'N/A'}ms</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDebugPanel;